import { graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';
import styled from 'styled-components';

import FlexColumn from '@ui/atoms/FlexColumn';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import fullWidth from '@ui/styled/FullWidth';
import padded from '@ui/styled/Padded';

import usePaymentMethod from '@hooks/usePaymentMethod';
import useSubscriptionInfo from '@hooks/useSubscriptionInfo';

import { keysToCamel } from '@utils/butter';

import { IPageProps } from '@folx-types/flow-steps';

import SwitchToYearlyMembership, {
	ISwitchToYearlyMembershipPageContent,
} from './SwitchToYearlyMembership.component';

interface ISwitchToYearlyMembershipPageProps extends IPageProps {}

const FullWidthColumn = fullWidth(FlexColumn);
const PaddedColumn = padded(FlexColumn);
const CardSkeleton = styled(Skeleton)`
	height: 23rem;
`;

const SwitchToYearlyMembershipSkeleton = () => (
	<FullWidthColumn gap="lg">
		<Skeleton height="md" fullWidth />
		<CardSkeleton fullWidth structure="single" />
		<PaddedColumn gap="sm" paddingLeft="md" paddingRight="md">
			<FlexColumn gap="xs">
				<Skeleton height="xs" fullWidth />
				<Skeleton height="xs" fullWidth />
				<Skeleton height="xs" fullWidth />
				<Skeleton height="xs" fullWidth />
				<Skeleton height="xs" width="xxxxxl" />
			</FlexColumn>
			<Skeleton height="xs" width="xxxxl" />
		</PaddedColumn>
		<PaddedColumn gap="xs" paddingLeft="md" paddingRight="md">
			<Skeleton height="lg" fullWidth />
			<Skeleton height="sm" fullWidth />
		</PaddedColumn>
	</FullWidthColumn>
);
const SwitchToYearlyMembershipPage: FC<ISwitchToYearlyMembershipPageProps> = ({ path }) => {
	const { pageContent, sharedMembershipContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "mssp-switch-to-yearly-membership-page" }) {
				title
				terms_copy
				terms_copy_free_trial
				primary_cta
				secondary_cta
				secondary_cta_free_trial
			}
			sharedMembershipContent: butterPage(slug: { eq: "shared-folx-membership-information" }) {
				folx_membership_option_yearly {
					title
					price
					badge
					description
					disclaimer
				}
			}
		}
	`);

	const refinedContent: ISwitchToYearlyMembershipPageContent = keysToCamel({
		...pageContent,
		...sharedMembershipContent,
	}) as ISwitchToYearlyMembershipPageContent;

	const { value: paymentMethod, loading: paymentMethodLoading } = usePaymentMethod();

	const { membership, loading: subscriptionLoading } = useSubscriptionInfo();

	return (
		<LoadingWrapper
			loading={paymentMethodLoading || subscriptionLoading}
			skeleton={<SwitchToYearlyMembershipSkeleton />}>
			<SwitchToYearlyMembership
				paymentMethod={paymentMethod}
				pageContent={refinedContent}
				isTrial={membership?.membershipSubscription.isTrial}
				path={path}
			/>
		</LoadingWrapper>
	);
};

export default SwitchToYearlyMembershipPage;
